import { useEffect, useState } from 'react';
import { init, viewport, isTMA } from '@telegram-apps/sdk';
import { mockInitData } from 'templates/initData';
import { verifyToken } from 'utils/jwt';
import axios from 'utils/axios';

declare global {
  interface Window {
    Telegram?: {
      WebApp?: {
        ready: () => void;
        expand: () => void;
        requestFullscreen: () => void;
        initData: string;
        initDataUnsafe?: {
          start_param?: string;
          [key: string]: unknown;
        };
      };
    };
  }
}

interface TelegramResponse {
  token: string;
  // Add other response properties as needed
}

interface UseTelegramTokenReturn {
  responseData: TelegramResponse | null;
}

const useTelegramToken = (): UseTelegramTokenReturn => {
  const [initData, setInitData] = useState<string | null>(null);
  const [responseData, setResponseData] = useState<TelegramResponse | null>(null);

  const useMockData = import.meta.env.VITE_USE_MOCK_INIT_DATA === 'true';

  const loadTelegramData = async (): Promise<void> => {
    if (useMockData) {
      const player1 = mockInitData.initDataPlayer1;
      setInitData(player1);
      return;
    }

    const tg = window.Telegram?.WebApp;
    if (!tg) {
      console.error('Telegram WebApp not found.');
      return;
    }

    tg.ready();
    tg.expand();
    tg.requestFullscreen();

    const currentSession = tg.initData;
    setInitData(currentSession);

    if (isTMA()) {
      init();
      if (viewport.expand.isAvailable()) viewport.expand();
      if (viewport.requestFullscreen.isAvailable()) await viewport.requestFullscreen();
    }
  };

  useEffect(() => {
    loadTelegramData();
  }, [useMockData]);

  useEffect(() => {
    if (!initData) return;

    const token = localStorage.getItem('token');
    if (verifyToken(token)) return;

    const sendInitDataToServer = async (): Promise<void> => {
      try {
        const response = await axios.post('/api/initdata', { initdata: initData }, { headers: { 'Content-Type': 'application/json' } });
        setResponseData(response.data);
      } catch (error) {
        console.error('Error sending initdata to the server:', error);
      }
    };

    sendInitDataToServer();
  }, [initData]);

  return { responseData };
};

export default useTelegramToken;
